import classNames from "classnames";
import React, { useMemo } from "react";

import { useEnableProactiveWarnings } from "featureFlags/hooks/useEnableProactiveWarnings";

import LightBulbIcon from "assets/raw/LightBulb.svg?react";

import TooltipMessage from "components/Tooltip/TooltipMessage/TooltipMessage";
import TooltipMessageDivider from "components/Tooltip/TooltipMessage/TooltipMessageDivider";

import { useDocuments } from "../../../../contexts/DocumentsContext/hooks/useDocuments";
import Warning from "../DocumentResults/ResultsControls/Warning/Warning";

import styles from "./ProactiveWarnings.module.scss";

export const useProactiveWarnings = ({
  queryStatementId,
  componentOptions = {},
}: {
  queryStatementId: string;
  componentOptions?: {
    greyedOut?: boolean;
  };
}) => {
  const { qsResults } = useDocuments();
  const enabledProactiveWarnings = useEnableProactiveWarnings();

  const results = qsResults[queryStatementId];

  const proactiveWarnings = useMemo(() => {
    const messages = [] as React.ReactElement[];

    if (!enabledProactiveWarnings) {
      return messages;
    }

    if (results?.performanceHints?.length) {
      results.performanceHints.forEach((hint, idx) => {
        if (idx > 0) messages.push(<TooltipMessageDivider />);
        messages.push(
          <TooltipMessage
            testId={`performance-hint-${idx}`}
            title={hint.issue_description}
            description={hint.issue_details}
            hint={hint.hint}
          />
        );
      });
    }

    return messages;
  }, [enabledProactiveWarnings, results?.performanceHints]);

  const hasProactiveWarnings = !!proactiveWarnings.length;

  const warningComponent = hasProactiveWarnings ? (
    <Warning
      messages={proactiveWarnings}
      placement="top-end"
      icon={
        <div
          className={classNames(styles.bulbIcon, {
            [styles.greyedOut]: componentOptions.greyedOut,
          })}
          data-testid="proactive-warning-icon"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <LightBulbIcon />
        </div>
      }
    />
  ) : null;

  return {
    hasProactiveWarnings,
    warningComponent,
    proactiveWarnings,
  };
};
